import { Controller } from "@hotwired/stimulus"

// Drafts of the new-project wizard (Kloepfer K8-5, Larry 2026-10-08). Larry
// stepped out of the wizard to look for a company and came back to an empty
// form. Now what's typed is saved as you go — a moment after each change, and
// when you leave the page — and comes back from the Draft card on the Projects
// page (or the banner in the wizard) with the step you were on. The team step
// is the one part re-added by hand: its companies and people are picked, not
// typed. Sits on the wizard's <form>; builder module only for now (Phelipe).
const SKIP = new Set(["authenticity_token", "utf8", "_method", "draft_id", "commit"])

export default class extends Controller {
  static targets = ["idField", "status"]
  static values = { saveUrl: String, phase: String, id: Number, step: Number, fields: Object, savedAt: String, savedLabel: String }

  connect() {
    this._onChange = (e) => { if (!this._teamField(e.target)) this._queue() }
    this.element.addEventListener("input", this._onChange)
    this.element.addEventListener("change", this._onChange)
    this._onLeave = () => this._save(true)
    window.addEventListener("pagehide", this._onLeave)
    document.addEventListener("turbo:before-visit", this._onLeave)
    if (this.idValue) {
      this._restore()
      this._paintSaved(this.savedAtValue)
    }
  }

  disconnect() {
    clearTimeout(this._timer)
    this.element.removeEventListener("input", this._onChange)
    this.element.removeEventListener("change", this._onChange)
    window.removeEventListener("pagehide", this._onLeave)
    document.removeEventListener("turbo:before-visit", this._onLeave)
  }

  _queue() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this._save(), 1500)
  }

  // ── What gets saved ────────────────────────────────────────────────
  _teamField(el) {
    return !!el?.closest?.('[data-controller~="team-card"], [data-controller~="owner-members"], [data-team-cards]')
  }

  _snapshot() {
    const out = {}
    for (const el of this.element.elements) {
      if (!el.name || el.disabled || SKIP.has(el.name)) continue
      if (["file", "password", "submit", "button", "reset"].includes(el.type)) continue
      if (this._teamField(el)) continue
      if ((el.type === "checkbox" || el.type === "radio") && !el.checked) continue
      if (el.multiple && el.tagName === "SELECT") { out[el.name] = [...el.selectedOptions].map(o => o.value); continue }
      out[el.name] = el.name in out ? [].concat(out[el.name], el.value) : el.value
    }
    return out
  }

  // Something the person typed or picked — not a prefilled default, so a
  // wizard merely opened never leaves a draft behind.
  _touched() {
    for (const el of this.element.elements) {
      if (!el.name || el.disabled || SKIP.has(el.name) || el.type === "hidden" || this._teamField(el)) continue
      if (el.type === "checkbox" || el.type === "radio") { if (el.checked !== el.defaultChecked) return true; continue }
      if (el.tagName === "SELECT") { if ([...el.options].some(o => o.selected !== o.defaultSelected)) return true; continue }
      if (typeof el.value === "string" && el.value.trim() !== "" && el.value !== el.defaultValue) return true
    }
    return false
  }

  async _save(leaving = false) {
    clearTimeout(this._timer)
    if (!this.idValue && !this._touched()) return
    if (this._saving && !leaving) { this._again = true; return }
    this._saving = true
    const wizard = this._wizard()
    const body = JSON.stringify({ id: this.idValue || null, phase: this.phaseValue, step: wizard?.currentStepValue || 0, data: this._snapshot() })
    try {
      const res = await fetch(this.saveUrlValue, { method: "POST", credentials: "same-origin", keepalive: leaving,
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" }, body })
      const data = res.ok ? await res.json().catch(() => null) : null
      if (data?.id) {
        this.idValue = data.id
        if (this.hasIdFieldTarget) this.idFieldTarget.value = data.id
        this._paintSaved(data.saved_at)
      }
    } catch (e) { /* the next change tries again */ }
    this._saving = false
    if (this._again) { this._again = false; this._queue() }
  }

  _paintSaved(iso) {
    if (!this.hasStatusTarget || !iso) return
    const d = new Date(iso)
    const time = isNaN(d) ? "" : d.toLocaleTimeString(document.documentElement.lang || undefined, { hour: "numeric", minute: "2-digit" })
    this.statusTarget.textContent = (this.savedLabelValue || "Draft saved · %{time}").replace("%{time}", time)
    this.statusTarget.hidden = false
  }

  // ── Coming back ────────────────────────────────────────────────────
  _restore() {
    const fields = this.fieldsValue || {}
    for (const [name, raw] of Object.entries(fields)) {
      const values = [].concat(raw)
      const els = [...this.element.querySelectorAll(`[name="${CSS.escape(name)}"]`)]
      if (!els.length) continue
      const hasBox = els.some(el => el.type === "checkbox" || el.type === "radio")
      let i = 0
      for (const el of els) {
        if (el.type === "checkbox" || el.type === "radio") { el.checked = values.includes(el.value); continue }
        if (el.type === "hidden" && hasBox) continue // Rails' hidden "0" twin of a check box
        if (el.multiple && el.tagName === "SELECT") { [...el.options].forEach(o => { o.selected = values.includes(o.value) }); continue }
        if (i >= values.length) break
        const v = values[i++]
        if (el._flatpickr) el._flatpickr.setDate(v, false)
        else el.value = v
      }
      // The other controllers (map, code check, dependent lists) follow the restored values.
      els.forEach(el => { el.dispatchEvent(new Event("input", { bubbles: true })); el.dispatchEvent(new Event("change", { bubbles: true })) })
    }
    const wizard = this._wizard()
    if (wizard && this.stepValue > 0) {
      // The saved step is the wizard's physical index; its reach is counted in
      // logical steps (a phase may skip some). Landing on Review & Create has
      // to build the summary too, as Next does — otherwise it says "Loading"
      // for good (Phelipe 2026-10-09).
      const logical = wizard._physicalToLogical(this.stepValue)
      if (logical === -1) return
      wizard._maxStepReached = Math.max(wizard._maxStepReached || 0, logical)
      wizard.currentStepValue = this.stepValue
      wizard.showStep(this.stepValue)
      if (logical === wizard._logicalStepCount - 1) wizard.buildReview()
    }
  }

  _wizard() {
    const el = this.element.closest('[data-controller~="project-wizard"]')
    return el ? this.application.getControllerForElementAndIdentifier(el, "project-wizard") : null
  }
}
